import {
  createContext,
  type PropsWithChildren,
  type RefObject,
  useCallback,
  useContext,
  useReducer
} from 'react';
import { clamp } from 'framer-motion';

function getDropdownTransform(anchor: DOMRect, content: DOMRect) {
  const pageMiddle = window.innerWidth * 0.5;
  const anchorStart = anchor.left;
  const anchorMiddle = anchor.left + anchor.width * 0.5;
  const anchorEnd = anchor.left + anchor.width;
  const contentStart = 0;
  const contentMiddle = content.width * 0.5;
  const contentEnd = content.width;

  const negativeMargin = content.width * 0.3;

  let offset = 0;
  switch (true) {
    // right align
    case anchorMiddle > pageMiddle:
      offset = anchorEnd - contentEnd + negativeMargin * 1.4;
      break;

    // left align
    case anchorMiddle < pageMiddle:
      offset = anchorStart - contentStart - negativeMargin * 0.25;
      break;

    // center align
    default:
      offset = anchorMiddle - contentMiddle;
  }

  const clampMargin = 16;
  const clampedOffset = clamp(
    clampMargin,
    window.innerWidth - clampMargin,
    offset
  );

  return clampedOffset;
}

function getRelativeTransforms(value: string, scope?: HTMLElement) {
  const documentScope = scope || document;
  const triggers = documentScope.querySelectorAll("[id*='trigger']");
  const els: Record<string, { anchor: Element; content: Element }> = {};
  for (const trigger of triggers) {
    const key = trigger.id.split('trigger-').at(-1);
    const contentNodeId = trigger?.getAttribute('aria-controls') ?? null;
    const content = contentNodeId
      ? document.getElementById(contentNodeId)
      : null;

    if (!key || !content) continue;

    els[key] = {
      anchor: trigger,
      content: content
    };
  }

  const transforms = Object.entries(els).reduce(
    (acc, [key, { anchor, content }]) => {
      acc[key] = getDropdownTransform(
        anchor.getBoundingClientRect(),
        content.getBoundingClientRect()
      );
      return acc;
    },
    {} as Record<string, number>
  );

  const activeTransform = transforms[value];
  const relativeTransforms = Object.entries(transforms).reduce(
    (acc, [key, transform]) => {
      acc[key] = transform - activeTransform;
      return acc;
    },
    {} as Record<string, number>
  );

  return relativeTransforms;
}

type State = {
  boundingBoxes: {
    content: DOMRect;
    trigger: DOMRect;
  } | null;

  relativeTransforms: Record<string, number>;
  reset: boolean;
  shouldAnimateSize: boolean;
  value: string | null;
};

const INITIAL_STATE: State = {
  boundingBoxes: null,
  value: null,
  shouldAnimateSize: false,
  reset: true,
  relativeTransforms: {}
};

function reducer(
  state: State,
  action:
    | { type: 'clear' }
    | {
        payload: Omit<State, 'shouldAnimateSize' | 'reset'>;
        type: 'set';
      }
): State {
  switch (action.type) {
    case 'set':
      return {
        ...state,
        ...action.payload,
        reset: false,
        shouldAnimateSize:
          !state.reset &&
          Boolean(state.boundingBoxes && action.payload.boundingBoxes)
      };

    case 'clear':
    default:
      return {
        ...state,
        value: null,
        shouldAnimateSize: false,
        reset: true
      };
  }
}

type DropDownContextValue = {
  handleValueChange: (v: string, scopeRef?: RefObject<HTMLElement>) => void;
  state: State;
};

const DropdownStateContext = createContext<
  DropDownContextValue['state'] | null
>(null);
const DropdownDispatchContext = createContext<
  DropDownContextValue['handleValueChange'] | null
>(null);

export function NavDropdownProvider({ children }: PropsWithChildren) {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  /**
   * Takes a Radix value such as `radix:r1:` and captures
   * corresponding bounding boxes.
   */
  const handleValueChange = useCallback(
    (value: string, scopeRef?: RefObject<HTMLElement>) => {
      if (!value) {
        dispatch({
          type: 'clear'
        });
        return;
      }

      const documentScope = scopeRef?.current || document;
      const triggerQuerySelector = `[id*='trigger-${value}']`;
      const triggerNode =
        documentScope.querySelector(triggerQuerySelector) ?? null;
      const contentNodeId = triggerNode?.getAttribute('aria-controls') ?? null;
      const contentNode = contentNodeId
        ? document.getElementById(contentNodeId)
        : null;

      if (!contentNode || !triggerNode) {
        dispatch({
          type: 'clear'
        });
        return;
      }

      const trigger = triggerNode.getBoundingClientRect();
      const content = contentNode.getBoundingClientRect();
      dispatch({
        type: 'set',
        payload: {
          value,
          boundingBoxes: {
            trigger,
            content
          },
          relativeTransforms: getRelativeTransforms(
            value,
            scopeRef?.current ?? undefined
          )
        }
      });
    },
    []
  );

  return (
    <DropdownDispatchContext.Provider value={handleValueChange}>
      <DropdownStateContext.Provider value={state}>
        {children}
      </DropdownStateContext.Provider>
    </DropdownDispatchContext.Provider>
  );
}

export function useNavDropdownContext() {
  const value = useContext(DropdownStateContext);

  if (value === null) {
    throw new Error(
      'must use useNavDropdownContext() within NavDropdownProvider'
    );
  }

  return value;
}

export function useNavDropdownDispatchContext() {
  const value = useContext(DropdownDispatchContext);

  if (value === null) {
    throw new Error(
      'must use useNavDropdownDispatchContext() within NavDropdownProvider'
    );
  }

  return value;
}

export function useNavDropdownTransform() {
  const state = useNavDropdownContext();

  const transform = state.boundingBoxes
    ? getDropdownTransform(
        state.boundingBoxes.trigger,
        state.boundingBoxes.content
      )
    : null;

  return transform;
}
